/**
 * API Service Layer for SM CCMS
 * Handles all communication with the Spring Boot backend
 * 
 * Security: Passwords are SHA-256 hashed before transmission
 */

import { hashPassword } from '../utils/crypto';

// API Base URL - can be overridden with environment variable (include scheme so fetch() uses absolute URL)
const API_BASE_URL_RAW = (process.env.REACT_APP_API_URL || '').trim() || 'ccms-838729948007.asia-south1.run.app';
const API_BASE_URL = (API_BASE_URL_RAW && API_BASE_URL_RAW.startsWith('http')) ? API_BASE_URL_RAW : `https://${API_BASE_URL_RAW}`;

// Session expiration callback - set by App.js
let onSessionExpired = null;

/**
 * Set the session expiration callback
 * @param {function} callback - Function to call when session expires
 */
export function setSessionExpiredCallback(callback) {
  onSessionExpired = callback;
}

/**
 * Custom API Error class
 */
class ApiError extends Error {
  constructor(message, status, code, data = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.data = data;
  }
}

/**
 * Make an API request with credentials (cookies)
 * @param {string} endpoint - API endpoint (e.g., '/api/auth/login/')
 * @param {object} options - Fetch options
 * @returns {Promise<object>} - Response data
 */
async function apiRequest(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const defaultOptions = {
    credentials: 'include', // Include cookies for session auth
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
  };
  
  const mergedOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  };
  
  try {
    const response = await fetch(url, mergedOptions);
    const data = await response.json();
    
    if (!response.ok) {
      // Check for session expiration (401 Unauthorized)
      if (response.status === 401 && onSessionExpired && !endpoint.includes('/login')) {
        onSessionExpired();
      }
      
      throw new ApiError(
        data.error || 'An error occurred',
        response.status,
        data.code || 'UNKNOWN_ERROR',
        data
      );
    }
    
    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    // Network error or JSON parse error
    throw new ApiError(
      error.message || 'Network error',
      0,
      'NETWORK_ERROR'
    );
  }
}

// ============================================
// Authentication APIs
// ============================================

/**
 * Get CSRF token from the server
 * Should be called before any POST/PUT/DELETE requests
 */
export async function getCsrfToken() {
  return apiRequest('/api/auth/csrf/', { method: 'GET' });
}

/**
 * Login with username and password
 * Password is SHA-256 hashed before transmission for security
 * 
 * @param {string} username 
 * @param {string} password - Plain text password (will be hashed)
 * @returns {Promise<object>} - User data on success
 */
export async function login(username, password) {
  // Hash password with SHA-256 before sending
  const hashedPassword = await hashPassword(password);
  
  return apiRequest('/api/auth/login/', {
    method: 'POST',
    body: JSON.stringify({ username, password: hashedPassword }),
  });
}

/**
 * Logout the current user
 */
export async function logout() {
  return apiRequest('/api/auth/logout/', {
    method: 'POST',
  });
}

/**
 * Check if the current session is authenticated
 * @returns {Promise<object>} - Auth status and user data
 */
export async function checkAuth() {
  return apiRequest('/api/auth/check/', { method: 'GET' });
}

/**
 * Get the current user's information
 * @returns {Promise<object>} - User data
 */
export async function getCurrentUser() {
  return apiRequest('/api/auth/me/', { method: 'GET' });
}

/**
 * Get the current user's role and permissions
 * @returns {Promise<object>} - Role and permissions data
 */
export async function getUserRole() {
  return apiRequest('/api/auth/role/', { method: 'GET' });
}

// ============================================
// User Management APIs (Super Admin Only)
// ============================================

/**
 * Get list of all users
 * @returns {Promise<object>} - List of users
 */
export async function getUsers() {
  return apiRequest('/api/auth/users/', { method: 'GET' });
}

/**
 * Create a new user
 * Password is SHA-256 hashed before transmission
 * 
 * @param {object} userData - User data (password will be hashed if present)
 * @returns {Promise<object>} - Created user
 */
export async function createUser(userData) {
  const dataToSend = { ...userData };
  
  // Hash password if present
  if (dataToSend.password) {
    dataToSend.password = await hashPassword(dataToSend.password);
  }
  
  return apiRequest('/api/auth/users/create/', {
    method: 'POST',
    body: JSON.stringify(dataToSend),
  });
}

/**
 * Update an existing user
 * Password is SHA-256 hashed before transmission if being updated
 * 
 * @param {string} userId - User ID
 * @param {object} userData - User data to update
 * @returns {Promise<object>} - Updated user
 */
export async function updateUser(userId, userData) {
  const dataToSend = { ...userData };
  
  // Hash password if being updated
  if (dataToSend.password) {
    dataToSend.password = await hashPassword(dataToSend.password);
  }
  
  return apiRequest(`/api/auth/users/${userId}/`, {
    method: 'PUT',
    body: JSON.stringify(dataToSend),
  });
}

/**
 * Delete a user
 * @param {number} userId - User ID
 * @returns {Promise<object>} - Deletion result
 */
export async function deleteUser(userId) {
  return apiRequest(`/api/auth/users/${userId}/delete/`, {
    method: 'DELETE',
  });
}

/**
 * Reset a user's password
 * Password is SHA-256 hashed before transmission
 * 
 * @param {number} userId - User ID
 * @param {string} newPassword - New password (will be hashed)
 * @returns {Promise<object>} - Reset result
 */
export async function resetUserPassword(userId, newPassword) {
  const hashedPassword = await hashPassword(newPassword);
  
  return apiRequest(`/api/auth/users/${userId}/reset-password/`, {
    method: 'POST',
    body: JSON.stringify({ password: hashedPassword }),
  });
}

// ============================================
// Dashboard APIs
// ============================================

/**
 * Get dashboard data for the current user
 * @returns {Promise<object>} - Dashboard data
 */
export async function getDashboardData() {
  return apiRequest('/api/auth/dashboard/', { method: 'GET' });
}

/**
 * Get SM dashboard data (SM Tender Management, SM Product Manager, Super Admin)
 * @returns {Promise<object>} - SM dashboard data
 */
export async function getSMDashboardData() {
  return apiRequest('/api/auth/dashboard/sm/', { method: 'GET' });
}

/**
 * Get analytics data (SM Tender Management, SM Product Manager, Super Admin)
 * @returns {Promise<object>} - Analytics data
 */
export async function getAnalyticsData() {
  return apiRequest('/api/auth/analytics/', { method: 'GET' });
}

/**
 * Get dashboard analytics charts and stats (role-aware)
 * @returns {Promise<object>} - { success, stats, charts }
 */
export async function getAnalyticsCharts() {
  return apiRequest('/api/auth/analytics/charts/', { method: 'GET' });
}

/**
 * Get combined audit trail from all collections (Super Admin only).
 * @param {number} [limit=300] - Max number of entries (capped at 500)
 * @returns {Promise<object>} - { success, audits, total }
 */
export async function getAuditTrail(limit = 300) {
  const params = new URLSearchParams();
  if (limit != null) params.append('limit', String(limit));
  const qs = params.toString();
  return apiRequest(`/api/auth/audit-trail/${qs ? '?' + qs : ''}`, { method: 'GET' });
}

// ============================================
// Company/Client Management APIs
// ============================================

/**
 * Get dropdown options for company form
 * @returns {Promise<object>} - Form options (industries, company_types, zones)
 */
export async function getCompanyFormOptions() {
  return apiRequest('/api/auth/companies/options/', { method: 'GET' });
}

/**
 * Get list of all companies
 * @param {object} filters - Optional filters (is_client, industry, company_type, zone, continent, region, search)
 * @returns {Promise<object>} - List of companies
 */
export async function getCompanies(filters = {}) {
  const params = new URLSearchParams();
  if (filters.is_client !== undefined && filters.is_client !== null && filters.is_client !== '') {
    params.append('is_client', filters.is_client);
  }
  if (filters.industry) params.append('industry', filters.industry);
  if (filters.company_type) params.append('company_type', filters.company_type);
  if (filters.zone) params.append('zone', filters.zone);
  if (filters.continent) params.append('continent', filters.continent);
  if (filters.region) params.append('region', filters.region);
  if (filters.search) params.append('search', filters.search);
  
  const queryString = params.toString();
  const endpoint = queryString 
    ? `/api/auth/companies/?${queryString}` 
    : '/api/auth/companies/';
  
  return apiRequest(endpoint, { method: 'GET' });
}

/**
 * Get a single company by ID
 * @param {string} companyId - Company ID
 * @returns {Promise<object>} - Company data
 */
export async function getCompany(companyId) {
  return apiRequest(`/api/auth/companies/${companyId}/`, { method: 'GET' });
}

/**
 * Create a new company
 * @param {object} companyData - Company data
 * @returns {Promise<object>} - Created company
 */
export async function createCompany(companyData) {
  return apiRequest('/api/auth/companies/create/', {
    method: 'POST',
    body: JSON.stringify(companyData),
  });
}

/**
 * Update an existing company
 * @param {string} companyId - Company ID
 * @param {object} companyData - Company data to update
 * @returns {Promise<object>} - Updated company
 */
export async function updateCompany(companyId, companyData) {
  return apiRequest(`/api/auth/companies/${companyId}/update/`, {
    method: 'PUT',
    body: JSON.stringify(companyData),
  });
}

/**
 * Delete a company
 * @param {string} companyId - Company ID
 * @returns {Promise<object>} - Deletion result
 */
export async function deleteCompany(companyId) {
  return apiRequest(`/api/auth/companies/${companyId}/delete/`, {
    method: 'DELETE',
  });
}

// ============================================
// Company Contact Management APIs
// ============================================

/**
 * Add a contact to a company
 * @param {string} companyId - Company ID
 * @param {object} contactData - Contact data (name, designation, phones[], emails[], notes)
 * @returns {Promise<object>} - Updated company
 */
export async function addCompanyContact(companyId, contactData) {
  return apiRequest(`/api/auth/companies/${companyId}/contacts/`, {
    method: 'POST',
    body: JSON.stringify(contactData),
  });
}

/**
 * Update a contact in a company
 * @param {string} companyId - Company ID
 * @param {string} contactId - Contact ID
 * @param {object} contactData - Contact data to update
 * @returns {Promise<object>} - Updated company
 */
export async function updateCompanyContact(companyId, contactId, contactData) {
  return apiRequest(`/api/auth/companies/${companyId}/contacts/${contactId}/`, {
    method: 'PUT',
    body: JSON.stringify(contactData),
  });
}

/**
 * Delete a contact from a company
 * @param {string} companyId - Company ID
 * @param {string} contactId - Contact ID
 * @returns {Promise<object>} - Updated company
 */
export async function deleteCompanyContact(companyId, contactId) {
  return apiRequest(`/api/auth/companies/${companyId}/contacts/${contactId}/`, {
    method: 'DELETE',
  });
}

// ============================================
// Brand Management APIs
// ============================================

/**
 * Get list of all brands
 * @param {string} search - Optional search term
 * @returns {Promise<object>} - List of brands
 */
export async function getBrands(search = '') {
  const params = search ? `?search=${encodeURIComponent(search)}` : '';
  return apiRequest(`/api/auth/brands/${params}`, { method: 'GET' });
}

/**
 * Get a single brand with its products
 * @param {string} brandId - Brand ID
 * @returns {Promise<object>} - Brand data with products
 */
export async function getBrand(brandId) {
  return apiRequest(`/api/auth/brands/${brandId}/`, { method: 'GET' });
}

/**
 * Create a new brand with optional logo
 * @param {FormData} formData - Form data with brand info and logo
 * @returns {Promise<object>} - Created brand
 */
export async function createBrand(formData) {
  const url = `${API_BASE_URL}/api/auth/brands/create/`;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    body: formData, // Don't set Content-Type, browser will set multipart boundary
  });
  const data = await response.json();
  if (!response.ok) {
    throw new ApiError(data.error || 'Failed to create brand', response.status, data.code);
  }
  return data;
}

/**
 * Update a brand
 * @param {string} brandId - Brand ID
 * @param {FormData} formData - Form data with brand info and logo
 * @returns {Promise<object>} - Updated brand
 */
export async function updateBrand(brandId, formData) {
  const url = `${API_BASE_URL}/api/auth/brands/${brandId}/update/`;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  const data = await response.json();
  if (!response.ok) {
    throw new ApiError(data.error || 'Failed to update brand', response.status, data.code);
  }
  return data;
}

/**
 * Delete a brand
 * @param {string} brandId - Brand ID
 * @returns {Promise<object>} - Deletion result
 */
export async function deleteBrand(brandId) {
  return apiRequest(`/api/auth/brands/${brandId}/delete/`, {
    method: 'DELETE',
  });
}

// ============================================
// Product Management APIs
// ============================================

/**
 * Get list of products
 * @param {object} filters - Optional filters (brand_id, search)
 * @returns {Promise<object>} - List of products
 */
export async function getProducts(filters = {}) {
  const params = new URLSearchParams();
  if (filters.brand_id) params.append('brand_id', filters.brand_id);
  if (filters.search) params.append('search', filters.search);
  
  const queryString = params.toString();
  const endpoint = queryString ? `/api/auth/products/?${queryString}` : '/api/auth/products/';
  return apiRequest(endpoint, { method: 'GET' });
}

/**
 * Get a single product
 * @param {string} productId - Product ID
 * @returns {Promise<object>} - Product data
 */
export async function getProduct(productId) {
  return apiRequest(`/api/auth/products/${productId}/`, { method: 'GET' });
}

/**
 * Create a new product with files
 * @param {FormData} formData - Form data with product info and files
 * @returns {Promise<object>} - Created product
 */
export async function createProduct(formData) {
  const url = `${API_BASE_URL}/api/auth/products/create/`;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  const data = await response.json();
  if (!response.ok) {
    throw new ApiError(data.error || 'Failed to create product', response.status, data.code);
  }
  return data;
}

/**
 * Update a product
 * @param {string} productId - Product ID
 * @param {FormData} formData - Form data with product info and files
 * @returns {Promise<object>} - Updated product
 */
export async function updateProduct(productId, formData) {
  const url = `${API_BASE_URL}/api/auth/products/${productId}/update/`;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  const data = await response.json();
  if (!response.ok) {
    throw new ApiError(data.error || 'Failed to update product', response.status, data.code);
  }
  return data;
}

/**
 * Delete a product
 * @param {string} productId - Product ID
 * @returns {Promise<object>} - Deletion result
 */
export async function deleteProduct(productId) {
  return apiRequest(`/api/auth/products/${productId}/delete/`, {
    method: 'DELETE',
  });
}

// ============================================
// Tender Management APIs
// ============================================

/**
 * Get list of tenders with optional filters
 * @param {object} filters - Optional filters (source, status, search, buyer_id)
 * @returns {Promise<object>} - List of tenders
 */
export async function getTenders(filters = {}) {
  const params = new URLSearchParams();
  if (filters.source) params.append('source', filters.source);
  if (filters.status) params.append('status', filters.status);
  if (filters.search) params.append('search', filters.search);
  if (filters.buyer_id) params.append('buyer_id', filters.buyer_id);
  
  const queryString = params.toString();
  const endpoint = queryString ? `/api/auth/tenders/?${queryString}` : '/api/auth/tenders/';
  return apiRequest(endpoint, { method: 'GET' });
}

/**
 * Get a single tender
 * @param {string} tenderId - Tender ID
 * @returns {Promise<object>} - Tender data
 */
export async function getTender(tenderId) {
  return apiRequest(`/api/auth/tenders/${tenderId}/`, { method: 'GET' });
}

/**
 * Get tender form options (sources, statuses, buyers, products)
 * @returns {Promise<object>} - Form options
 */
export async function getTenderOptions() {
  return apiRequest('/api/auth/tenders/options/', { method: 'GET' });
}

/**
 * Get tender statistics
 * @returns {Promise<object>} - Tender stats
 */
export async function getTenderStats() {
  return apiRequest('/api/auth/tenders/stats/', { method: 'GET' });
}

/**
 * Create a new tender
 * @param {object} data - Tender data
 * @returns {Promise<object>} - Created tender
 */
export async function createTender(data) {
  return apiRequest('/api/auth/tenders/create/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

/**
 * Update a tender
 * @param {string} tenderId - Tender ID
 * @param {object} data - Tender data
 * @returns {Promise<object>} - Updated tender
 */
export async function updateTender(tenderId, data) {
  return apiRequest(`/api/auth/tenders/${tenderId}/update/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

/**
 * Delete a tender
 * @param {string} tenderId - Tender ID
 * @returns {Promise<object>} - Deletion result
 */
export async function deleteTender(tenderId) {
  return apiRequest(`/api/auth/tenders/${tenderId}/delete/`, {
    method: 'DELETE',
  });
}

/**
 * Move tender to Participating (Go Ahead)
 * @param {string} tenderId - Tender ID
 * @returns {Promise<object>} - Updated tender
 */
export async function tenderGoAhead(tenderId) {
  return apiRequest(`/api/auth/tenders/${tenderId}/go-ahead/`, {
    method: 'POST',
  });
}

/**
 * Move tender to No-Go
 * @param {string} tenderId - Tender ID
 * @returns {Promise<object>} - Updated tender
 */
export async function tenderNoGo(tenderId) {
  return apiRequest(`/api/auth/tenders/${tenderId}/no-go/`, {
    method: 'POST',
  });
}

/**
 * Get list of participating tenders
 * @param {object} filters - Optional filters
 * @returns {Promise<object>} - List of participating tenders
 */
export async function getParticipatingTenders(filters = {}) {
  const params = new URLSearchParams();
  if (filters.source) params.append('source', filters.source);
  if (filters.status) params.append('status', filters.status);
  if (filters.search) params.append('search', filters.search);
  
  const queryString = params.toString();
  const endpoint = queryString ? `/api/auth/tenders-participating/?${queryString}` : '/api/auth/tenders-participating/';
  return apiRequest(endpoint, { method: 'GET' });
}

/**
 * Master list: all participating tenders with optional year/month and sort.
 * @param {object} opts - { year, month (1-12), sort_by, sort_dir, search }
 * @returns {Promise<object>} - { success, tenders, total, can_manage }
 */
export async function getParticipatingTendersMaster(opts = {}) {
  const params = new URLSearchParams();
  if (opts.year != null) params.append('year', opts.year);
  if (opts.month != null) params.append('month', opts.month);
  if (opts.sort_by) params.append('sort_by', opts.sort_by);
  if (opts.sort_dir) params.append('sort_dir', opts.sort_dir);
  if (opts.search) params.append('search', opts.search);
  const queryString = params.toString();
  const endpoint = queryString ? `/api/auth/tenders-participating/master/?${queryString}` : '/api/auth/tenders-participating/master/';
  return apiRequest(endpoint, { method: 'GET' });
}

/**
 * Get participating tender options
 * @returns {Promise<object>} - Form options
 */
export async function getParticipatingTenderOptions() {
  return apiRequest('/api/auth/tenders-participating/options/', { method: 'GET' });
}

/**
 * Get upcoming submission deadlines for participating tenders.
 * @param {number} days - Look-ahead window (default 7)
 * @returns {Promise<object>} - { success, deadlines: [...], total, days }
 */
export async function getUpcomingDeadlines(days = 7) {
  return apiRequest(`/api/auth/tenders-participating/upcoming-deadlines/?days=${days}`, { method: 'GET' });
}

/**
 * Get status change timeline for a participating tender.
 * @param {string} tenderId
 * @returns {Promise<object>} - { success, status_history: [...], total }
 */
export async function getStatusHistory(tenderId) {
  return apiRequest(`/api/auth/tenders-participating/${tenderId}/status-history/`, { method: 'GET' });
}

/**
 * Get recent status changes for participating tenders (e.g. won/lost in last N days).
 * @param {string} status - 'won' or 'lost'
 * @param {number} days - Look-back window (default 30)
 */
export async function getRecentStatusChanges(status = 'won', days = 30) {
  return apiRequest(`/api/auth/tenders-participating/status-changes/?status=${status}&days=${days}`, { method: 'GET' });
}

/**
 * Get a single participating tender by ID
 * @param {string} tenderId - Participating tender ID
 * @returns {Promise<object>} - Tender data
 */
export async function getParticipatingTender(tenderId) {
  return apiRequest(`/api/auth/tenders-participating/${tenderId}/`, { method: 'GET' });
}

/**
 * Update a participating tender
 * @param {string} tenderId - Participating tender ID
 * @param {object} data - Update payload (e.g. status, title, description, portal_url)
 * @returns {Promise<object>} - Updated tender
 */
export async function updateParticipatingTender(tenderId, data) {
  return apiRequest(`/api/auth/tenders-participating/${tenderId}/update/`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ============================================
// Notifications
// ============================================

export async function getNotifications() {
  return apiRequest('/api/auth/notifications/', { method: 'GET' });
}

export async function getUnreadNotificationCount() {
  return apiRequest('/api/auth/notifications/unread-count/', { method: 'GET' });
}

export async function markNotificationRead(notificationId) {
  return apiRequest(`/api/auth/notifications/${notificationId}/read/`, { method: 'POST' });
}

export async function markAllNotificationsRead() {
  return apiRequest('/api/auth/notifications/mark-all-read/', { method: 'POST' });
}

// ============================================
// Assignable Users & My Tasks
// ============================================

export async function getAssignableUsers() {
  return apiRequest('/api/auth/users/assignable/', { method: 'GET' });
}

export async function getMyTasks() {
  return apiRequest('/api/auth/my-tasks/', { method: 'GET' });
}

// ============================================
// Archive & Delete Participating Tenders
// ============================================

export async function archiveWonTender(tenderId) {
  return apiRequest(`/api/auth/tenders-participating/${tenderId}/archive/`, { method: 'POST' });
}

export async function deleteLostTender(tenderId) {
  return apiRequest(`/api/auth/tenders-participating/${tenderId}/`, { method: 'DELETE' });
}

export async function getArchivedTenders(year = null) {
  const params = year ? `?year=${year}` : '';
  return apiRequest(`/api/auth/tenders-archived/${params}`, { method: 'GET' });
}

export async function getArchiveYears() {
  return apiRequest('/api/auth/tenders-archived/years/', { method: 'GET' });
}

// ============================================
// EMD (Earnest Money Deposit) APIs
// ============================================

/**
 * Get EMD for a participating tender (returns null if none exists; no auto-create)
 * @param {string} tenderId - Participating tender ID
 * @returns {Promise<object>} - { success, emd }
 */
export async function getEmdForTender(tenderId) {
  return apiRequest(`/api/auth/tenders-participating/${tenderId}/emd/`, { method: 'GET' });
}

/**
 * Create EMD for a participating tender (explicit create; fails if EMD already exists)
 * @param {string} tenderId - Participating tender ID
 * @returns {Promise<object>} - { success, message, emd }
 */
export async function createEmdForTender(tenderId) {
  return apiRequest(`/api/auth/tenders-participating/${tenderId}/emd/create/`, { method: 'POST' });
}

/**
 * Update EMD for a participating tender
 * @param {string} tenderId - Participating tender ID
 * @param {object} data - EMD fields (emd_type, emd_amount, beneficiary_name, validity_end_date, type-specific fields)
 * @returns {Promise<object>} - { success, message, emd }
 */
export async function updateEmdForTender(tenderId, data) {
  return apiRequest(`/api/auth/tenders-participating/${tenderId}/emd/update/`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Upload PDF document for an EMD (e.g. scanned DD/BG/receipt). Allowed when EMD is in CREATED status.
 * @param {string} tenderId - Participating tender ID
 * @param {File} file - PDF file
 * @returns {Promise<object>} - { success, message, emd } (emd includes pdf_url when present)
 */
export async function uploadEmdPdf(tenderId, file) {
  const formData = new FormData();
  formData.append('file', file);
  const url = `${API_BASE_URL}/api/auth/tenders-participating/${tenderId}/emd/upload-pdf/`;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    body: formData,
    headers: { Accept: 'application/json' },
  });
  const data = await response.json();
  if (!response.ok) {
    throw new ApiError(data.error || 'Failed to upload EMD PDF', response.status, data.code);
  }
  return data;
}

/**
 * Submit EMD (CREATED → SUBMITTED). Must be complete and valid.
 * @param {string} tenderId - Participating tender ID
 * @returns {Promise<object>} - { success, message, emd }
 */
export async function submitEmdForTender(tenderId) {
  return apiRequest(`/api/auth/tenders-participating/${tenderId}/emd/submit/`, {
    method: 'POST',
  });
}

/**
 * Get all EMDs (global list), optionally filtered by type
 * @param {object} filters - Optional { emd_type }
 * @returns {Promise<object>} - { success, emds, total }
 */
export async function getAllEmds(filters = {}) {
  const params = new URLSearchParams();
  if (filters.emd_type) params.append('emd_type', filters.emd_type);
  const queryString = params.toString();
  const endpoint = queryString ? `/api/auth/emds/?${queryString}` : '/api/auth/emds/';
  return apiRequest(endpoint, { method: 'GET' });
}

/**
 * Get EMD form options (emd_types, emd_statuses)
 * @returns {Promise<object>} - { success, emd_types, emd_statuses }
 */
export async function getEmdOptions() {
  return apiRequest('/api/auth/emds/options/', { method: 'GET' });
}

/**
 * Get list of No-Go tenders
 * @param {object} filters - Optional filters
 * @returns {Promise<object>} - List of no-go tenders
 */
export async function getNoGoTenders(filters = {}) {
  const params = new URLSearchParams();
  if (filters.source) params.append('source', filters.source);
  if (filters.search) params.append('search', filters.search);
  
  const queryString = params.toString();
  const endpoint = queryString ? `/api/auth/tenders-nogo/?${queryString}` : '/api/auth/tenders-nogo/';
  return apiRequest(endpoint, { method: 'GET' });
}

// ============================================
// Pipeline APIs
// ============================================

export async function getTenderPipeline() {
  return apiRequest('/api/auth/tenders/pipeline/', { method: 'GET' });
}

export async function moveTenderStage(tenderId, fromCollection, toStage) {
  return apiRequest('/api/auth/tenders/pipeline/move/', {
    method: 'POST',
    body: JSON.stringify({ tender_id: tenderId, from_collection: fromCollection, to_stage: toStage }),
  });
}

// ============================================
// Task CRUD – Discovery Tenders
// ============================================

export async function addDiscoveryTask(tenderId, taskData) {
  return apiRequest(`/api/auth/tenders/${tenderId}/tasks/`, {
    method: 'POST',
    body: JSON.stringify(taskData),
  });
}

export async function updateDiscoveryTask(tenderId, taskId, taskData) {
  return apiRequest(`/api/auth/tenders/${tenderId}/tasks/${taskId}/`, {
    method: 'PUT',
    body: JSON.stringify(taskData),
  });
}

export async function deleteDiscoveryTask(tenderId, taskId) {
  return apiRequest(`/api/auth/tenders/${tenderId}/tasks/${taskId}/`, {
    method: 'DELETE',
  });
}

// ============================================
// Task CRUD – Participating Tenders
// ============================================

export async function addParticipatingTask(tenderId, taskData) {
  return apiRequest(`/api/auth/tenders-participating/${tenderId}/tasks/`, {
    method: 'POST',
    body: JSON.stringify(taskData),
  });
}

export async function updateParticipatingTask(tenderId, taskId, taskData) {
  return apiRequest(`/api/auth/tenders-participating/${tenderId}/tasks/${taskId}/`, {
    method: 'PUT',
    body: JSON.stringify(taskData),
  });
}

export async function deleteParticipatingTask(tenderId, taskId) {
  return apiRequest(`/api/auth/tenders-participating/${tenderId}/tasks/${taskId}/`, {
    method: 'DELETE',
  });
}

// ============================================
// Technical Bid APIs
// ============================================

/**
 * Assign a technical bid task to a product manager
 * @param {string} tenderId - Participating tender ID
 * @param {object} data - { assignedToId, instructions, dueDate }
 * @returns {Promise<object>} - { success, technical_bid }
 */
export async function assignTechnicalBid(tenderId, data) {
  return apiRequest(`/api/auth/tenders-participating/${tenderId}/technical-bid/assign/`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Get technical bid for a participating tender
 * @param {string} tenderId - Participating tender ID
 * @returns {Promise<object>} - { success, technical_bid } or 404
 */
export async function getTechnicalBidForTender(tenderId) {
  return apiRequest(`/api/auth/tenders-participating/${tenderId}/technical-bid/`, { method: 'GET' });
}

/**
 * List technical bids with filters
 * @param {object} filters - { assigned_to_me, assigned_by_me, tender_id }
 * @returns {Promise<object>} - { success, technical_bids, total }
 */
export async function listTechnicalBids(filters = {}) {
  const params = new URLSearchParams();
  if (filters.assigned_to_me) params.append('assigned_to_me', 'true');
  if (filters.assigned_by_me) params.append('assigned_by_me', 'true');
  if (filters.tender_id) params.append('tender_id', filters.tender_id);
  const queryString = params.toString();
  const endpoint = queryString ? `/api/auth/technical-bids/?${queryString}` : '/api/auth/technical-bids/';
  return apiRequest(endpoint, { method: 'GET' });
}

/**
 * Get a single technical bid by ID
 * @param {string} id - Technical bid ID
 * @returns {Promise<object>} - { success, technical_bid }
 */
export async function getTechnicalBid(id) {
  return apiRequest(`/api/auth/technical-bids/${id}/`, { method: 'GET' });
}

/**
 * Update technical bid (status, instructions)
 * @param {string} id - Technical bid ID
 * @param {object} data - Update payload
 * @returns {Promise<object>} - { success, technical_bid }
 */
export async function updateTechnicalBid(id, data) {
  return apiRequest(`/api/auth/technical-bids/${id}/`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Upload PDF for technical bid
 * @param {string} id - Technical bid ID
 * @param {File} file - PDF file
 * @returns {Promise<object>} - { success, technical_bid }
 */
export async function uploadTechnicalBidPdf(id, file) {
  const formData = new FormData();
  formData.append('file', file);
  const url = `${API_BASE_URL}/api/auth/technical-bids/${id}/upload-pdf/`;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    body: formData,
    headers: { Accept: 'application/json' },
  });
  if (response.status === 401 && onSessionExpired) onSessionExpired();
  let data;
  try {
    data = await response.json();
  } catch {
    throw new ApiError(response.statusText || 'Failed to upload PDF', response.status, 'INVALID_RESPONSE');
  }
  if (!response.ok) {
    throw new ApiError(data.error || 'Failed to upload PDF', response.status, data.code);
  }
  return data;
}

/**
 * Upload DOCX for technical bid
 * @param {string} id - Technical bid ID
 * @param {File} file - DOCX file
 * @returns {Promise<object>} - { success, technical_bid }
 */
export async function uploadTechnicalBidDocx(id, file) {
  const formData = new FormData();
  formData.append('file', file);
  const url = `${API_BASE_URL}/api/auth/technical-bids/${id}/upload-docx/`;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    body: formData,
    headers: { Accept: 'application/json' },
  });
  if (response.status === 401 && onSessionExpired) onSessionExpired();
  let data;
  try {
    data = await response.json();
  } catch {
    throw new ApiError(response.statusText || 'Failed to upload DOCX', response.status, 'INVALID_RESPONSE');
  }
  if (!response.ok) {
    throw new ApiError(data.error || 'Failed to upload DOCX', response.status, data.code);
  }
  return data;
}

/**
 * Approve technical bid (Tender Manager)
 * @param {string} id - Technical bid ID
 * @returns {Promise<object>} - { success, technical_bid }
 */
export async function approveTechnicalBid(id) {
  return apiRequest(`/api/auth/technical-bids/${id}/approve/`, { method: 'POST' });
}

/**
 * Reject technical bid (Tender Manager)
 * @param {string} id - Technical bid ID
 * @param {object} data - { feedback } optional
 * @returns {Promise<object>} - { success, technical_bid }
 */
export async function rejectTechnicalBid(id, data = {}) {
  return apiRequest(`/api/auth/technical-bids/${id}/reject/`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Get audit trail for technical bid
 * @param {string} id - Technical bid ID
 * @returns {Promise<object>} - { success, audit }
 */
export async function getTechnicalBidAudit(id) {
  return apiRequest(`/api/auth/technical-bids/${id}/audit/`, { method: 'GET' });
}

/**
 * Get list of product managers (for assignment dropdown)
 * @returns {Promise<object>} - { success, users }
 */
export async function getProductManagers() {
  return apiRequest('/api/auth/users/product-managers/', { method: 'GET' });
}

// ============================================
// Financial Bid APIs
// ============================================

export async function assignFinancialBid(tenderId, data) {
  return apiRequest(`/api/auth/tenders-participating/${tenderId}/financial-bid/assign/`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getFinancialBidForTender(tenderId) {
  return apiRequest(`/api/auth/tenders-participating/${tenderId}/financial-bid/`, { method: 'GET' });
}

export async function listFinancialBids(filters = {}) {
  const params = new URLSearchParams();
  if (filters.assigned_to_me) params.append('assigned_to_me', 'true');
  if (filters.assigned_by_me) params.append('assigned_by_me', 'true');
  if (filters.tender_id) params.append('tender_id', filters.tender_id);
  const queryString = params.toString();
  const endpoint = queryString ? `/api/auth/financial-bids/?${queryString}` : '/api/auth/financial-bids/';
  return apiRequest(endpoint, { method: 'GET' });
}

export async function getFinancialBid(id) {
  return apiRequest(`/api/auth/financial-bids/${id}/`, { method: 'GET' });
}

export async function updateFinancialBid(id, data) {
  return apiRequest(`/api/auth/financial-bids/${id}/`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function uploadFinancialBidPdf(id, file) {
  const formData = new FormData();
  formData.append('file', file);
  const url = `${API_BASE_URL}/api/auth/financial-bids/${id}/upload-pdf/`;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    body: formData,
    headers: { Accept: 'application/json' },
  });
  if (response.status === 401 && onSessionExpired) onSessionExpired();
  let data;
  try {
    data = await response.json();
  } catch {
    throw new ApiError(response.statusText || 'Failed to upload PDF', response.status, 'INVALID_RESPONSE');
  }
  if (!response.ok) {
    throw new ApiError(data.error || 'Failed to upload PDF', response.status, data.code);
  }
  return data;
}

export async function uploadFinancialBidExcel(id, file) {
  const formData = new FormData();
  formData.append('file', file);
  const url = `${API_BASE_URL}/api/auth/financial-bids/${id}/upload-excel/`;
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    body: formData,
    headers: { Accept: 'application/json' },
  });
  if (response.status === 401 && onSessionExpired) onSessionExpired();
  let data;
  try {
    data = await response.json();
  } catch {
    throw new ApiError(response.statusText || 'Failed to upload Excel', response.status, 'INVALID_RESPONSE');
  }
  if (!response.ok) {
    throw new ApiError(data.error || 'Failed to upload Excel', response.status, data.code);
  }
  return data;
}

export async function approveFinancialBid(id) {
  return apiRequest(`/api/auth/financial-bids/${id}/approve/`, { method: 'POST' });
}

export async function rejectFinancialBid(id, data = {}) {
  return apiRequest(`/api/auth/financial-bids/${id}/reject/`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getFinancialBidAudit(id) {
  return apiRequest(`/api/auth/financial-bids/${id}/audit/`, { method: 'GET' });
}

export async function getFinanceManagers() {
  return apiRequest('/api/auth/users/finance-managers/', { method: 'GET' });
}

// ============================================
// Tender Audit Trail
// ============================================

export async function getTenderAuditTrail(tenderId) {
  return apiRequest(`/api/auth/tenders-participating/${tenderId}/audit/`, { method: 'GET' });
}

// ============================================
// Tender document URLs (auth-protected; backend redirects to signed URL)
// ============================================

/**
 * Base URL with scheme for use in links (so cookies are sent to same host as API).
 */
function getApiBaseUrlForLinks() {
  const base = process.env.REACT_APP_API_URL || API_BASE_URL;
  return base.startsWith('http') ? base : `https://${base}`;
}

/**
 * URL for technical bid document (PDF or DOCX). Use for redirect; for credentialed fetch use getTechnicalBidDocumentSignedUrl.
 */
export function getTechnicalBidDocumentUrl(id, type) {
  const base = getApiBaseUrlForLinks();
  return `${base}/api/auth/technical-bids/${id}/document/${type}`;
}

/**
 * Fetch signed URL for technical bid document with credentials, then open in new tab. Use this so cookies are sent (works cross-origin).
 */
export async function getTechnicalBidDocumentSignedUrl(id, type) {
  const res = await apiRequest(`/api/auth/technical-bids/${id}/document/${type}?format=json`, { method: 'GET' });
  if (res && res.success && res.url) return res.url;
  throw new Error(res?.message || 'Failed to get document link');
}

/**
 * URL for financial bid document (PDF or excel). Use for redirect; for credentialed fetch use getFinancialBidDocumentSignedUrl.
 */
export function getFinancialBidDocumentUrl(id, type) {
  const base = getApiBaseUrlForLinks();
  return `${base}/api/auth/financial-bids/${id}/document/${type}`;
}

/**
 * Fetch signed URL for financial bid document with credentials, then open in new tab. Use this so cookies are sent (works cross-origin).
 */
export async function getFinancialBidDocumentSignedUrl(id, type) {
  const res = await apiRequest(`/api/auth/financial-bids/${id}/document/${type}?format=json`, { method: 'GET' });
  if (res && res.success && res.url) return res.url;
  throw new Error(res?.message || 'Failed to get document link');
}

// ============================================
// Utility Exports
// ============================================

export { ApiError, apiRequest, API_BASE_URL };

// Default export for convenience
const api = {
  // Auth
  getCsrfToken,
  login,
  logout,
  checkAuth,
  getCurrentUser,
  getUserRole,
  // User Management
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  resetUserPassword,
  getNotifications,
  getUnreadNotificationCount,
  markNotificationRead,
  markAllNotificationsRead,
  getAssignableUsers,
  getMyTasks,
  // Dashboard
  getDashboardData,
  getSMDashboardData,
  getAnalyticsData,
  getAnalyticsCharts,
  // Company Management
  getCompanyFormOptions,
  getCompanies,
  getCompany,
  createCompany,
  updateCompany,
  deleteCompany,
  addCompanyContact,
  updateCompanyContact,
  deleteCompanyContact,
  // Brand Management
  getBrands,
  getBrand,
  createBrand,
  updateBrand,
  deleteBrand,
  // Product Management
  getProducts,
  getProduct,
  createProduct,
  updateProduct,
  deleteProduct,
  // Tender Management
  getTenders,
  getTender,
  getTenderOptions,
  getTenderStats,
  createTender,
  updateTender,
  deleteTender,
  tenderGoAhead,
  tenderNoGo,
  getParticipatingTenders,
  getParticipatingTendersMaster,
  getParticipatingTenderOptions,
  getUpcomingDeadlines,
  getStatusHistory,
  getRecentStatusChanges,
  getParticipatingTender,
  updateParticipatingTender,
  archiveWonTender,
  deleteLostTender,
  getArchivedTenders,
  getArchiveYears,
  getEmdForTender,
  updateEmdForTender,
  uploadEmdPdf,
  submitEmdForTender,
  getAllEmds,
  getEmdOptions,
  getNoGoTenders,
  // Pipeline
  getTenderPipeline,
  moveTenderStage,
  // Task CRUD
  addDiscoveryTask,
  updateDiscoveryTask,
  deleteDiscoveryTask,
  addParticipatingTask,
  updateParticipatingTask,
  deleteParticipatingTask,
  // Technical Bid
  assignTechnicalBid,
  getTechnicalBidForTender,
  listTechnicalBids,
  getTechnicalBid,
  updateTechnicalBid,
  uploadTechnicalBidPdf,
  uploadTechnicalBidDocx,
  approveTechnicalBid,
  rejectTechnicalBid,
  getTechnicalBidAudit,
  getProductManagers,
  // Financial Bid
  assignFinancialBid,
  getFinancialBidForTender,
  listFinancialBids,
  getFinancialBid,
  updateFinancialBid,
  uploadFinancialBidPdf,
  uploadFinancialBidExcel,
  approveFinancialBid,
  rejectFinancialBid,
  getFinancialBidAudit,
  getFinanceManagers,
  // Tender Audit
  getTenderAuditTrail,
  // Utilities
  ApiError,
  apiRequest,
  API_BASE_URL,
};

export default api;
